import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { useUserRoles } from './useUserRoles';
import { getLiberiaCurrentUser } from '@/utils/liberiaApi';

// Session storage key written by `AuthContainer` / `RoleSelectionStep` when the
// user picks which portal to sign into (for multi-role accounts). Kept in sync
// with `src/components/auth/AuthContainer.tsx` and `src/pages/Index.tsx`.
const SELECTED_ROLE_KEY = 'qls_selected_role';

// Read the role the user chose at sign-in, if any. Returns null if nothing is
// stored or the payload is malformed; callers must also verify the role is
// still valid against the user's current `roleAccess` list before using it.
function readSelectedRoleFromSession(): string | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const stored = sessionStorage.getItem(SELECTED_ROLE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as unknown;
    if (typeof parsed === 'string') {
      const t = parsed.trim();
      return t.length > 0 ? t : null;
    }
    if (parsed && typeof parsed === 'object' && 'role' in parsed) {
      const r = (parsed as { role?: unknown }).role;
      return typeof r === 'string' && r.trim().length > 0 ? r.trim() : null;
    }
    return null;
  } catch {
    return null;
  }
}

export const useUserRole = () => {
  const { user } = useAuth();
  const { data: roleAccess = [], isLoading: rolesLoading } = useUserRoles();

  const query = useQuery({
    queryKey: ['user-role', user?.id],
    enabled: Boolean(user?.id),
    staleTime: 30_000,
    queryFn: async () => {
      if (!user) {
        return null;
      }

      const currentUser = await getLiberiaCurrentUser({
        user,
        role: null,
        hasAddressGenerationAccess: true,
      });

      return currentUser.primaryRole || currentUser.coarseRole || currentUser.roles[0] || 'user';
    },
  });

  const serverRole = query.data ?? null;

  // A user can be assigned multiple roles, but can only be signed into ONE
  // portal per session. The role picked at the role-selection step is
  // persisted in sessionStorage; honour that here so every consumer of this
  // hook (Dashboard button, portal-scoped headers, role-based guards, etc.)
  // reflects the portal the user actually chose at sign-in rather than
  // silently falling back to their backend-side primary portal and
  // "switching" them out of the portal they signed into.
  //
  // Safety checks:
  //   - Only use the stored role if it matches an entry in the user's current
  //     `roleAccess` list. This handles cases where an admin revokes the role
  //     mid-session or the stored payload is stale.
  //   - Fall back to the server-resolved `primaryRole` when nothing is
  //     stored, so first-render / single-role / pre-selection paths behave
  //     exactly as before.
  const role = useMemo(() => {
    const selected = readSelectedRoleFromSession();
    if (selected) {
      // While /me/roles is still loading, roleAccess defaults to [] and we would drop the stored portal —
      // that left x-dev-roles without road_infrastructure and admin announce POST returned 403.
      if (rolesLoading && roleAccess.length === 0) {
        return selected;
      }
      if (roleAccess.some((entry) => entry.role === selected)) {
        return selected;
      }
    }
    return serverRole;
  }, [serverRole, roleAccess, rolesLoading]);

  /** Used by admin analytics, session admin, etc. — must match multi-role users. */
  const hasAdminAccess = useMemo(() => {
    if (role === 'admin' || role === 'qls_admin') return true;
    return roleAccess.some((entry) => entry.role === 'admin' || entry.role === 'qls_admin');
  }, [role, roleAccess]);

  /** Coarse gate for liberiaApi context; explicit feature grants still enforced server-side. */
  const hasAddressGenerationAccess = useMemo(() => {
    if (role === 'admin' || role === 'qls_admin' || role === 'partner') return true;
    return roleAccess.some((entry) => entry.role === 'admin' || entry.role === 'qls_admin' || entry.role === 'partner');
  }, [role, roleAccess]);

  return {
    role,
    loading: query.isLoading || rolesLoading,
    error: query.error instanceof Error ? query.error.message : undefined,
    hasAdminAccess,
    hasAddressGenerationAccess,
  };
};
